import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays, X } from "lucide-react";
import { nb } from "date-fns/locale";
import { addDays, endOfMonth, endOfYear, format, startOfMonth, startOfYear, subMonths, parseISO } from "date-fns";
import { cn } from "@/lib/utils";

const iso = (d) => format(d, "yyyy-MM-dd");

const PRESETS = [
  { key: "denne_maaned", label: "Denne måneden", range: () => [startOfMonth(new Date()), endOfMonth(new Date())] },
  { key: "neste_30", label: "Neste 30 dager", range: () => [new Date(), addDays(new Date(), 30)] },
  { key: "neste_maaned", label: "Neste måned", range: () => { const n = addDays(endOfMonth(new Date()), 1); return [startOfMonth(n), endOfMonth(n)]; } },
  { key: "forrige_maaned", label: "Forrige måned", range: () => { const p = subMonths(new Date(), 1); return [startOfMonth(p), endOfMonth(p)]; } },
  { key: "i_aar", label: "I år", range: () => [startOfYear(new Date()), endOfYear(new Date())] },
];

export const PeriodPicker = ({ from, to, onChange, className, align = "start" }) => {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState({ from: from ? parseISO(from) : undefined, to: to ? parseISO(to) : undefined });

  useEffect(() => {
    setRange({ from: from ? parseISO(from) : undefined, to: to ? parseISO(to) : undefined });
  }, [from, to]);

  const label = useMemo(() => {
    if (!from && !to) return "Periode";
    const preset = PRESETS.find((p) => { const [a, b] = p.range(); return iso(a) === from && iso(b) === to; });
    if (preset) return preset.label;
    const f = from ? format(parseISO(from), "d. MMM yyyy", { locale: nb }) : "…";
    const t = to ? format(parseISO(to), "d. MMM yyyy", { locale: nb }) : "…";
    return `${f} – ${t}`;
  }, [from, to]);

  const apply = (a, b) => {
    onChange(a ? iso(a) : "", b ? iso(b) : "");
    setOpen(false);
  };

  const active = !!(from || to);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={cn("justify-start font-normal min-w-0", active && "border-primary text-foreground", className)} data-testid="invoice-period-button" aria-label="Velg periode">
          <CalendarDays className="h-4 w-4 mr-2 shrink-0 text-muted-foreground" />
          <span className="truncate">{label}</span>
          {active ? (
            <span role="button" tabIndex={0} aria-label="Nullstill periode" className="ml-auto pl-2 text-muted-foreground hover:text-foreground" onClick={(e) => { e.stopPropagation(); apply(undefined, undefined); }} onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); apply(undefined, undefined); } }} data-testid="invoice-period-clear"><X className="h-3.5 w-3.5" /></span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-auto p-0 bg-card">
        <div className="flex flex-col sm:flex-row">
          <div className="flex flex-col gap-1 p-3 border-b sm:border-b-0 sm:border-r min-w-[170px]">
            <p className="px-2 pb-1 text-xs font-medium text-muted-foreground">Hurtigvalg</p>
            {PRESETS.map((p) => (
              <Button key={p.key} variant="ghost" size="sm" className="justify-start" onClick={() => { const [a, b] = p.range(); apply(a, b); }} data-testid={`period-preset-${p.key}`}>{p.label}</Button>
            ))}
          </div>
          <div className="p-2">
            <Calendar mode="range" locale={nb} weekStartsOn={1} selected={range} onSelect={(r) => setRange(r || {})} numberOfMonths={1} defaultMonth={range.from || new Date()} />
            <div className="flex items-center justify-between px-2 pb-2 gap-2">
              <Button variant="ghost" size="sm" onClick={() => apply(undefined, undefined)}>Nullstill</Button>
              <Button size="sm" disabled={!range.from} onClick={() => apply(range.from, range.to || range.from)} data-testid="period-apply">Bruk</Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
